import * as React from 'react';
import { CancellationType, Res, Permission, TicketDTO } from 'core/api';
import { Command } from 'pos/common';
import { PhoneOnly, PosScreen, Header, Content, Footer, BackActionButton } from 'pos/ui';
import { Result } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { Message, Modal } from 'core/ui-next/modal';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { z } from 'zod';
import { BasicButton } from 'core/ui-next';
import { Box, VStack } from '@chakra-ui/react';
import { EnumUtils } from 'core/utils';


const cancellationTypeSchema = z.object({
	name: z.string(),
	value: z.number()
});

const stateSchema = z.object({
	cancellationType: cancellationTypeSchema.nullable().refine(val => val !== null, { message: Res.Msg.MSG_VALUE_REQUIRED() }),
	reason: z.string().max(255, { message: Res.Msg.MSG_MAX_LENGTH_EXCEEDED(255) })
});

type State = z.infer<typeof stateSchema>;

type Props = Modal.Actions<{ type: CancellationType, reason: string }>;

function GetTicketCancellationReason(props: Props) {
	const { cancel, close } = props;
	const { Form, Select, TextArea } = useControlsFor<State>();
	const formRef = React.useRef<FormRef<State>>(null);

	const initialValue: State = {
		cancellationType: null as any,
		reason: ''
	};

	const cancellationOptions = EnumUtils.getNamesAndValues(CancellationType);

	const onOk = async () => {
		const result = await formRef.current!.getValue();
		if (!result.ok)
			return;

		close({ type: result.value.cancellationType!.value, reason: result.value.reason });
	};

	const onCancel = React.useCallback(async () => cancel(), [cancel]);
	return (
		<PosScreen screenId={'GET_TICKET_CANCELLATION_REASON'} size={'small'} useKeyPad={false}>
			<Header title={Res.Web.CANCEL_TICKET()} onClose={cancel} />
			<Content>
				<VStack width={'full'} height={'full'} padding={'6'} gap={'6'} overflow={'auto'}>
					<Box width={'full'}>
						<Form ref={formRef} initialValue={initialValue} schema={stateSchema}>
							<VStack width={'full'} gap={'4'}>
								<Select linkTo={'cancellationType'} label={Res.Web.CANCEL_TICKET_SELECT_TYPE_LBL()} placeholder={Res.Web.CANCEL_TICKET_SELECT_TYPE_LBL()} options={cancellationOptions} renderOption={opt => opt && CancellationType.toString(opt.value)} valueGetter={opt => opt && CancellationType.toString(opt.value)} required />
								<TextArea linkTo={'reason'} placeholder={Res.Web.CANCEL_TICKET_NOTES_PLACEHOLDER()} rows={8} maxLength={255} />
							</VStack>
						</Form>
					</Box>
					<BasicButton type='success' width={'full'} text={Res.Common.OK_BTN()} onClick={onOk} />
				</VStack>
			</Content>
			<Footer>
				<PhoneOnly>
					<BackActionButton action={onCancel} />
				</PhoneOnly>
			</Footer>
		</PosScreen>
	);
}

declare global {
	interface CommandType {
		cancelTicket: (params: Params) => Promise<Result>
	}
}

type Params = {
	getTicket: () => TicketDTO,
	cancel: (type: CancellationType, reason: string) => Promise<void>
	release: () => Promise<void>
}

Command.register('cancelTicket', Preconditions.ensure(Permission.Ticket_Cancel), async (params: Params): Promise<Result> => {

	if (!params.getTicket().IsInUse) {
		await params.release();
		return Result.ok();
	}

	if (params.getTicket().PrepaidAmount !== 0) {
		await Message.show(Res.Msg.MSG_CANNOT_CANCEL_A_TICKET_WITH_BALANCE_OTHER_THAN_ZERO());
		return Result.error();
	}

	const result = await Modal.show(GetTicketCancellationReason);

	if (!result.ok)
		return Result.error();

	await params.cancel(result.value.type, result.value.reason);
	return Result.ok();
});

